import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { useCategories } from '../../common/hooks/useCategories';
import { useCurrentPage } from '../../common/hooks/useCurrentPage';
import { useOnWidgetExpanded } from '../../common/hooks/useOnWidgetExpanded';
import { helpCenterSlice } from '../../common/reducers/helpCenter';
import { PresentedState } from '../../common/reducers/types';
import { Page } from '../../common/types';

const pageToPresentedState: Record<Page, PresentedState> = {
  [Page.Chatbot]: PresentedState.Chatbot,
  [Page.Home]: PresentedState.Homepage,
  [Page.Categories]: PresentedState.AllHelpTopics,
  [Page.Category]: PresentedState.SubCategoriesList,
  [Page.Search]: PresentedState.SearchResults,
  [Page.Article]: PresentedState.SingleArticle,
  [Page.RequestSuccessManagerForm]: PresentedState.RequestSuccessManagerForm,
};

export const usePresentedState = (): void => {
  const dispatch = useDispatch();
  const currentPage = useCurrentPage();
  const { currentCategory } = useCategories();

  const setPresentedState = useCallback(
    (value: PresentedState) => {
      dispatch(helpCenterSlice.actions.setPresentedState(value));
    },
    [dispatch],
  );

  const getCurrentPresentedState = useCallback((): PresentedState => {
    const isSolutionList =
      !currentCategory?.children || currentCategory?.children.length === 0;

    if (currentPage === Page.Category && isSolutionList) {
      return PresentedState.SolutionList;
    } else {
      return pageToPresentedState[currentPage];
    }
  }, [currentCategory, currentPage]);

  useOnWidgetExpanded((expanded: boolean) => {
    if (expanded) {
      setPresentedState(getCurrentPresentedState());
    } else {
      setPresentedState(PresentedState.WidgetButton);
    }
  });

  useEffect(() => {
    // when `currentPage` is `Page.Category` presentedState can be `SubCategoriesList` or `SolutionList`.
    // we need to wait for the currentPage to load to decide if it is a `SubCategoriesList` or `SolutionList`.
    if (currentPage === Page.Category && !currentCategory) {
      return;
    }

    setPresentedState(getCurrentPresentedState());
  }, [currentCategory, currentPage]);
};
